import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

import { Button } from '../../../components/Buttons';
import { DownloadSelect, FilterSelect, Search } from '../../../components/Filters';
import { ParamChanges, updateParams } from '../../../helpers/urlParams';
import CurrencyFilter from '../../common/CurrencyFilter';
import DateRangeFilter from './DateRangeFilter';
import NetAmountFilter from './NetAmountFilter';
import StateFilter from './StateFilter';

import { History, Location } from 'history';
import RefreshFilterButton from '../../../components/Filters/RefreshFilterButton';
import { FilterComponentWrapper, FilterRowWrapper, FiltersContainer, FiltersWrapper, RightWrapper, Wrapper } from '../../../components/Filters/elements';

export interface StateFilterProps extends WithTranslation {
    queryParams: URLSearchParams;
    onChange: (changes: { [key: string]: string[] }) => void;
}

const updateFilterParams = (queryParams: URLSearchParams, changes: ParamChanges) => {
    // remove paging when filter changes
    const nonPagingChanges = { ...changes, starting_after: undefined };
    return updateParams(queryParams, nonPagingChanges);
};

const filterConfigs = {
    'created_at.lte': {
        component: DateRangeFilter,
        keys: ['created_at.lte'],
    },
    draft_status: {
        component: StateFilter,
        keys: ['status'],
    },
    'net_amount.gte': {
        component: NetAmountFilter,
        keys: ['net_amount.gte', 'net_amount.lte'],
    },
    currency: {
        component: CurrencyFilter,
        keys: ['currency'],
    },
};
type FilterKey = keyof typeof filterConfigs;

const extraFilterProps = (_: FiltersProps): { [name: string]: { [key: string]: any } } => {
    return {};
};

const getConfigNamesFromQueryParams = (queryParamsKeys: string[]) => {
    const entries = Object.entries(filterConfigs);
    const matchingEntries = entries.filter(([_, config]) => {
        const matchingKeys = config.keys.filter((configKey) => queryParamsKeys.includes(configKey));
        return matchingKeys.length > 0;
    });
    return matchingEntries.map(([name, _]) => name);
};

interface FiltersProps extends WithTranslation {
    location: Location;
    history: History;
    refresh: () => void;
    createCsv: () => void;
    createPdf: () => void;
}

interface FiltersState {
    pending: FilterKey | undefined;
    search: string | undefined;
}

class Filters extends Component<FiltersProps, FiltersState> {
    constructor(props: FiltersProps, context: any) {
        super(props, context);
        const { location } = this.props;
        const queryParams = new URLSearchParams(location.search.substring(1));
        this.state = {
            pending: undefined,
            search: queryParams.get('search') || undefined,
        };
        this.setPending = this.setPending.bind(this);
        this.setSearch = this.setSearch.bind(this);
    }

    setPending(pending: FilterKey | undefined) {
        this.setState({ pending });
    }

    setSearch(search: string | undefined) {
        this.setState({ search });
    }

    render() {
        const { t, location, history, refresh, createCsv, createPdf } = this.props;
        const { pending, search } = this.state;
        const queryParams = new URLSearchParams(location.search.substring(1));
        const queryParamKeys = [...queryParams.keys()];
        const visibleFilterNames = [...getConfigNamesFromQueryParams(queryParamKeys), pending];
        const addableFilters = Object.keys(filterConfigs).filter((key) => !visibleFilterNames.includes(key));

        return (
            <Wrapper>
                <FilterRowWrapper>
                    <FiltersWrapper>
                        <FilterSelect
                            name="filterSelect"
                            disabled={!(!pending && addableFilters.length > 0)}
                            options={addableFilters.map((key) => (
                                <Button
                                    className="alt tiny"
                                    onClick={() => this.setPending(key as FilterKey)}
                                    key={`add-filter-${key}`}
                                >
                                    {t(`orders.filter.${key}.add_filter_button`)}
                                </Button>
                            ))}
                        />
                    </FiltersWrapper>
                    <RightWrapper>
                        <Search
                            name="search"
                            placeholder={t('orders.filter.search_placeholder')}
                            defaultValue={search || ''}
                            onChange={(value) => {
                                this.setSearch(value);
                            }}
                            onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
                                // search on enter
                                if (event.code === 'Enter' || event.key === 'Enter' || event.keyCode === 13) {
                                    const nextParams = updateFilterParams(queryParams, { search: this.state.search });
                                    history.push(`${location.pathname}?${nextParams.toString()}`);
                                }
                            }}
                            onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
                                // Wrapped in a try catch since this is supposedly an experimental browser API
                                try {
                                    const pasted = event.clipboardData.getData('Text');
                                    if (pasted) {
                                        const nextParams = updateFilterParams(queryParams, { search: pasted });
                                        history.push(`${location.pathname}?${nextParams.toString()}`);
                                    }
                                } catch (error) {
                                    // ignore error
                                }
                            }}
                            onBlur={() => {}}
                            hideIcon={true}
                            debounceTimeoutMs={0}
                        />
                        <FilterComponentWrapper>
                            <DownloadSelect
                                name="download-settlements"
                                options={[
                                    {
                                        format: '.csv',
                                        icon: 'tabular',
                                        onClick: createCsv,
                                    },
                                    {
                                        format: '.pdf',
                                        icon: 'pdf',
                                        onClick: createPdf,
                                    },
                                ]}
                            />
                            <RefreshFilterButton onClick={refresh} title={t('orders.filter.refresh')} />
                        </FilterComponentWrapper>
                    </RightWrapper>
                </FilterRowWrapper>
                <FiltersContainer>
                    {visibleFilterNames
                        .filter((key): key is FilterKey => !!key && key in filterConfigs)
                        .map((key) => {
                            const FilterComponent = filterConfigs[key].component;
                            const componentExtraFilterProps = extraFilterProps(this.props)[key];
                            return (
                                <FilterComponentWrapper key={`order-filter-${key}`}>
                                    <FilterComponent
                                        {...componentExtraFilterProps}
                                        queryParams={queryParams}
                                        onChange={(value) => {
                                            const nextParams = updateFilterParams(queryParams, value);
                                            history.push(`${location.pathname}?${nextParams.toString()}`);
                                            if (key === pending) {
                                                this.setPending(undefined);
                                            }
                                        }}
                                    />
                                </FilterComponentWrapper>
                            );
                        })}

                </FiltersContainer>
            </Wrapper>
        );
    }
}
export default withTranslation()<React.ComponentType<FiltersProps>>(Filters);
